import type { GenAiRequestHistoryRound } from '@/api/v4/genai-request-history.api';
import type { IOption } from '@/types';

import type { ExampleCompany } from '../explore.state';

export type ExploreRoundFilterDelta = {
  kind:
    | 'locationAdded'
    | 'locationRemoved'
    | 'referencesAdded'
    | 'referencesRemoved';
  items: string[];
};

const locationLabels = (round: GenAiRequestHistoryRound): string[] => [
  ...round.locationContinent.map(location => location.shortDescription),
  ...round.locationCountry.map(location => location.shortDescription),
];

const companyLabels = (round: GenAiRequestHistoryRound): string[] =>
  round.companies
    .map(company => company.name)
    .filter((name): name is string => Boolean(name));

const listDiff = (previous: string[], current: string[]) => {
  const added = current.filter(item => !previous.includes(item));
  const removed = previous.filter(item => !current.includes(item));
  return { added, removed };
};

export const isFilterOnlyRound = ({
  rounds,
  roundIndex,
}: {
  rounds: GenAiRequestHistoryRound[];
  roundIndex: number;
}): boolean => {
  if (roundIndex <= 0) {
    return false;
  }
  const previous = rounds[roundIndex - 1];
  const current = rounds[roundIndex];
  if (!previous || !current) {
    return false;
  }
  return previous.prompt === current.prompt;
};

export const getPromptSinceRoundNumber = ({
  rounds,
  roundIndex,
}: {
  rounds: GenAiRequestHistoryRound[];
  roundIndex: number;
}): number => {
  for (let index = roundIndex; index > 0; index -= 1) {
    const previous = rounds[index - 1];
    const current = rounds[index];
    if (!previous || !current) {
      continue;
    }
    if (current.prompt !== previous.prompt) {
      return current.roundNumber;
    }
  }
  return rounds[0]?.roundNumber ?? 1;
};

export const getRoundFilterDeltas = ({
  previous,
  current,
}: {
  previous: GenAiRequestHistoryRound;
  current: GenAiRequestHistoryRound;
}): ExploreRoundFilterDelta[] => {
  const locationDiff = listDiff(
    locationLabels(previous),
    locationLabels(current),
  );
  const companyDiff = listDiff(companyLabels(previous), companyLabels(current));
  const deltas: ExploreRoundFilterDelta[] = [];

  if (locationDiff.added.length) {
    deltas.push({ kind: 'locationAdded', items: locationDiff.added });
  }
  if (locationDiff.removed.length) {
    deltas.push({ kind: 'locationRemoved', items: locationDiff.removed });
  }
  if (companyDiff.added.length) {
    deltas.push({ kind: 'referencesAdded', items: companyDiff.added });
  }
  if (companyDiff.removed.length) {
    deltas.push({ kind: 'referencesRemoved', items: companyDiff.removed });
  }

  return deltas;
};

const optionLabels = (options: IOption[]): string[] =>
  options.map(option => option.label);

const exampleCompanyLabels = (companies: ExampleCompany[]): string[] =>
  companies.map(company => company.name).filter(Boolean);

export const getPendingFilterDeltas = ({
  defaultExampleCompanies,
  defaultLocations,
  updatedExampleCompanies,
  updatedLocations,
}: {
  defaultExampleCompanies: ExampleCompany[];
  defaultLocations: IOption[];
  updatedExampleCompanies: ExampleCompany[];
  updatedLocations: IOption[];
}): ExploreRoundFilterDelta[] => {
  const locationDiff = listDiff(
    optionLabels(defaultLocations),
    optionLabels(updatedLocations),
  );
  const companyDiff = listDiff(
    exampleCompanyLabels(defaultExampleCompanies),
    exampleCompanyLabels(updatedExampleCompanies),
  );
  const deltas: ExploreRoundFilterDelta[] = [];

  if (locationDiff.added.length) {
    deltas.push({ kind: 'locationAdded', items: locationDiff.added });
  }
  if (locationDiff.removed.length) {
    deltas.push({ kind: 'locationRemoved', items: locationDiff.removed });
  }
  if (companyDiff.added.length) {
    deltas.push({ kind: 'referencesAdded', items: companyDiff.added });
  }
  if (companyDiff.removed.length) {
    deltas.push({ kind: 'referencesRemoved', items: companyDiff.removed });
  }

  return deltas;
};
